import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, CircularProgress, Grid, Button } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import Product from '../components/Product';
import { useLocation, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

const OccasionProducts = () => {
  const { user, api } = useAuth();
  const { occasion } = useParams();
  const location = useLocation();
  const [products, setProducts] = useState(location.state?.products || []);
  const [loading, setLoading] = useState(!location.state?.products);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoad, setInitialLoad] = useState(!location.state?.products);
  const [activeCategory, setActiveCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const observer = useRef();
  const theme = useTheme();

  // Set categories based on user gender
  useEffect(() => {
    if (user?.gender) {
      if (user.gender.toLowerCase() === 'women') {
        setCategories([
          { id: 'all', name: 'All' },
          { id: 'dresses', name: 'Dresses' },
          { id: 'tops', name: 'Tops' },
          { id: 'pants', name: 'Pants' },
          { id: 'skirts', name: 'Skirts' },
          { id: 'outerwear', name: 'Outerwear' },
          { id: 'shoes', name: 'Shoes' },
          { id: 'bags', name: 'Bags' },
          { id: 'accessories', name: 'Accessories' },
          { id: 'swimwear', name: 'Swimwear' },
        ]);
      } else if (user.gender.toLowerCase() === 'men') {
        setCategories([
          { id: 'all', name: 'All' },
          { id: 'tops', name: 'Tops' },
          { id: 'pants', name: 'Pants' },
          { id: 'outerwear', name: 'Outerwear' },
          { id: 'suits', name: 'Suits' },
          { id: 'shoes', name: 'Shoes' },
          { id: 'accessories', name: 'Accessories' },
          { id: 'underwear', name: 'Underwear' },
          { id: 'swimwear', name: 'Swimwear' },
        ]);
      }
    }
  }, [user?.gender]);

  // Format occasion name for display
  const formatOccasionName = (name) => {
    return name
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get emoji for occasion
  const getOccasionEmoji = (occasion) => {
    const emojiMap = {
      'streetwear': '🛹',
      'smart-casual': '👔',
      'smart casual': '👔',
      'minimalist': '✨',
      'modern classic': '🎩',
      'casual chic': '👕',
      'rock chic': '🎸',
      'modern casual': '👟',
      'athleisure': '🏃',
      'contemporary': '🌟',
      'preppy': '📚'
    };
    console.log(occasion)
    
    const key = Object.keys(emojiMap).find(k => occasion.toLowerCase().includes(k));
    return key ? emojiMap[key] : '👗';
  };

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setPage(1);
    setProducts([]);
    setHasMore(true);
    setInitialLoad(true);
  };

  const lastProductElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // If it's the first page and we have initial products from state, use those
        if (page === 1 && location.state?.products && activeCategory === 'all' && !initialLoad) {
          setProducts(location.state.products);
          setHasMore(true); // We'll check for more products on next page load
          setLoading(false);
          return;
        }

        // Convert occasion name to match backend format
        const formattedOccasion = location.state?.initialOccasion || occasion;
        
        const response = await api.get(`/api/products/occasions/${formattedOccasion}`, {
          params: {
            page,
            category: activeCategory,
            per_page: 20
          }
        });

        const newProducts = response.data.products || [];
        const totalProducts = response.data.total || 0;
        const totalPages = response.data.total_pages || 1;
        
        // Update hasMore based on total pages
        setHasMore(page < totalPages);
        
        setProducts(prevProducts => {
          // If it's a new category or first page, replace products
          if (page === 1) return newProducts;
          // Otherwise append new products
          return [...prevProducts, ...newProducts];
        });

        setInitialLoad(false);
      } catch (err) {
        console.error('Error fetching occasion products:', err);
        setError('Failed to load products. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchProducts();
    }
  }, [user, api, page, occasion, activeCategory, location.state, initialLoad]);

  return (
    <Box sx={{ mt: 10, px: 3, maxWidth: 600, mx: 'auto', pb: 8 }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
        {formatOccasionName(occasion)} {getOccasionEmoji(occasion)}
      </Typography>

      {/* Category Carousel */}
      <Box 
        sx={{ 
          mb: 3, 
          pb: 1,
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            display: 'none'
          }
        }}
      >
        {categories.map((category) => (
          <Button
            key={category.id}
            onClick={() => handleCategoryChange(category.id)}
            sx={{
              mr: 2,
              px: 2,
              py: 1,
              color: activeCategory === category.id ? 'black' : theme.palette.grey[400],
              backgroundColor: 'white',
              border: 'none',
              borderRadius: '10px',
              position: 'relative',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'white',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100%',
                borderRadius: '10px',
                height: 3,
                backgroundColor: activeCategory === category.id ? 'primary.main' : 'transparent',
                transition: 'width 0.2s ease',
                width: activeCategory === category.id ? `${category.name.length * 8}px` : 0,
              }
            }}
          >
            {category.name}
          </Button>
        ))}
      </Box>

      {initialLoad && loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2}>
            {products.map((product, index) => {
              // Handle different image URL structures
              let imageUrl = '';
              
              // Try media.images structure first
              if (product.media?.images && product.media.images.length > 0) {
                const primaryImage = product.media.images.find(img => img.isPrimary)?.url;
                imageUrl = primaryImage || product.media.images[0]?.url;
              }
              
              // Fallback to image_url if exists
              if (!imageUrl && product.image_url) {
                imageUrl = product.image_url;
              }
              
              // Fallback to imageUrl if exists
              if (!imageUrl && product.imageUrl) {
                imageUrl = product.imageUrl;
              }

              // Get original price and calculate discount if available
              const originalPrice = product.original_price;
              let discountPercentage = product.discount_percentage;
              if (!discountPercentage && originalPrice && product.price) {
                discountPercentage = Math.round(((originalPrice - product.price) / originalPrice) * 100);
              }

              return (
                <Grid 
                  item 
                  xs={6} 
                  sm={6} 
                  md={6} 
                  key={product.id}
                  ref={index === products.length - 1 ? lastProductElementRef : null}
                >
                  <Product 
                    id={product.id}
                    image={imageUrl}
                    brand={product.brand?.name}
                    name={product.title}
                    price={product.price}
                    originalPrice={originalPrice}
                    discountPercentage={discountPercentage > 0 ? discountPercentage : undefined}
                    productDetails={product.product_details}
                    variants={product.variants}
                    category={product.category}
                    color={product.variants?.[0]?.color}
                    product={product}
                  />
                </Grid>
              );
            })}
          </Grid>

          {error && (
            <Typography color="error" sx={{ mt: 4, textAlign: 'center' }}>
              {error}
            </Typography>
          )}

          {!initialLoad && loading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4, mb: 2 }}>
              <CircularProgress size={30} />
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                Loading more products...
              </Typography>
            </Box>
          )}

          {!hasMore && products.length > 0 && (
            <Typography 
              variant="body2" 
              sx={{ 
                textAlign: 'center', 
                mt: 4, 
                color: 'text.secondary',
                borderTop: '1px solid',
                borderColor: 'divider',
                pt: 2
              }}
            >
              You've reached the end of the list
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default OccasionProducts; 